import { Component, ComponentType } from "@/types";
import { BaseCommandHandler, CommandResult, PropertyChange } from "../BaseCommandHandler";

export class DataTextCommandHandler extends BaseCommandHandler {
  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE 1: PRESET MAPS
  // ═══════════════════════════════════════════════════════════════════════════

  // Phase 1.1: Font Size Presets
  private readonly fontSizePresets: Record<string, string> = {
    tiny: '10px', xs: '10px', small: '12px', sm: '12px',
    normal: '16px', medium: '18px', md: '18px', large: '24px', lg: '24px',
    huge: '32px', xl: '32px', massive: '48px', xxl: '48px',
    giant: '64px', '3xl': '64px'
  };

  // Phase 1.3: Semantic Colors
  private readonly semanticColors: Record<string, string> = {
    success: '#059669', positive: '#059669', good: '#059669', profit: '#059669',
    error: '#DC2626', danger: '#DC2626', negative: '#DC2626', loss: '#DC2626',
    warning: '#D97706', caution: '#D97706', alert: '#D97706',
    info: '#2563EB', primary: '#2563EB',
    neutral: '#6B7280', muted: '#9CA3AF', secondary: '#6B7280'
  };

  // Known measurement units (ordered longest-first within groups to prevent partial matching)
  private readonly knownUnits: string[] = [
    // Temperature
    '°C', '°F', '°K',
    // Pressure
    'mmHg', 'inHg', 'PSI', 'psi', 'kPa', 'hPa', 'mbar', 'bar', 'atm', 'Pa',
    // Speed
    'km/h', 'mph', 'kn', 'm/s', 'ft/s',
    // Frequency
    'MHz', 'kHz', 'GHz', 'THz', 'Hz', 'rpm', 'RPM',
    // Electrical
    'mAh', 'kWh', 'MWh', 'mA', 'kW', 'MW', 'mV', 'kV',
    'Wh', 'Ah', 'VA', 'W', 'V', 'A',
    // Sound / signal
    'dBm', 'dB',
    // Mass
    'mcg', 'mg', 'kg', 'oz', 'lb', 'ton', 'g',
    // Volume
    'mL', 'ml', 'gal', 'qt', 'pt', 'fl oz', 'L',
    // Length
    'mm', 'cm', 'km', 'in', 'ft', 'yd', 'mi', 'nm', 'μm', 'm',
    // Time
    'ms', 'μs', 'ns', 'min', 'hr', 'sec', 's',
    // Data
    'Gbps', 'Mbps', 'kbps', 'bps',
    'TB', 'GB', 'MB', 'KB', 'kB',
    // Concentration / misc
    'ppm', 'ppb', 'mol', 'lux', 'lx', 'cd',
    // Common symbols
    '%', '$', '€', '£', '¥',
  ];

  // Phase 1.4: Font Weight Presets
  private readonly fontWeightPresets: Record<string, number> = {
    thin: 100, hairline: 100, light: 300, normal: 400,
    medium: 500, semibold: 600, bold: 700, heavy: 800, black: 900
  };

  constructor() {
    super();
    this.initializePatterns();
  }

  private initializePatterns(): void {
    // ═══════════════════════════════════════════════════════════════════════════
    // VALUE COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |change )?value (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { value: match[1] } })
    );

    this.commandPatterns.set(
      /(?:set |change )?value (?:to )?(\d+(?:\.\d+)?)/i,
      (match) => ({ properties: { value: parseFloat(match[1]) } })
    );

    this.commandPatterns.set(
      /(?:set |change )?empty ?value (?:text )?(?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { emptyValueText: match[1] } })
    );

    this.commandPatterns.set(
      /(?:set |change )?placeholder (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { emptyValueText: match[1] } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // LABEL COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |change )?label (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { label: match[1] } })
    );

    this.commandPatterns.set(
      /show label/i,
      () => ({ properties: { showLabel: true } })
    );

    this.commandPatterns.set(
      /hide label/i,
      () => ({ properties: { showLabel: false } })
    );

    this.commandPatterns.set(
      /(?:put |move |place )?label (?:on |to )?(?:the )?(above|below|left|right)/i,
      (match) => ({ properties: { labelPosition: match[1].toLowerCase() as 'above' | 'below' | 'left' | 'right' } })
    );

    this.commandPatterns.set(
      /(?:set |change )?label (?:color|colour) (?:to )?(.+)/i,
      (match) => {
        const colorInput = match[1].trim().toLowerCase();
        // Check for semantic colors first (Phase 1.3 support)
        const semanticColor = this.semanticColors[colorInput];
        if (semanticColor) {
          return { properties: { labelColor: semanticColor } };
        }
        const colorValue = this.parseColorValue(match[1]);
        return colorValue ? { properties: { labelColor: colorValue } } : {};
      }
    );

    this.commandPatterns.set(
      /(?:set |change )?label (?:font )?size (?:to )?(\d+(?:\.\d+)?)(px|rem|em)?/i,
      (match) => ({ properties: { labelFontSize: `${match[1]}${match[2] || 'px'}` } })
    );

    this.commandPatterns.set(
      /(?:make )?label (bold|bolder)/i,
      () => ({ properties: { labelFontWeight: 700 } })
    );

    this.commandPatterns.set(
      /(?:make )?label (normal|regular)/i,
      () => ({ properties: { labelFontWeight: 400 } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // UNIT COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |add |change )?unit (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { unit: match[1] } })
    );

    // Known units pattern (dynamically built, longest-first to prevent partial matching)
    const escapedUnits = this.knownUnits.map(u => u.replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&'));
    const knownUnitsPattern = new RegExp(
      `(?:set |add |change )?unit (?:to )?(${escapedUnits.join('|')})(?:\\s|$)`, 'i'
    );
    this.commandPatterns.set(
      knownUnitsPattern,
      (match) => ({ properties: { unit: match[1] } })
    );

    // Unquoted unit catch-all: "add unit PSI", "set unit to kPa"
    // Negative lookahead prevents matching "unit color blue", "unit font size 14px",
    // and semantic color keywords like "unit warning" (which should route to unitColor)
    const semanticExclusions = Object.keys(this.semanticColors).join('|');
    const unitCatchAllPattern = new RegExp(
      `(?:set |add |change )?unit (?:to )?(?!(?:colou?r|font|size|${semanticExclusions})\\b)([^\\s"']+)`, 'i'
    );
    this.commandPatterns.set(
      unitCatchAllPattern,
      (match) => ({ properties: { unit: match[1] } })
    );

    this.commandPatterns.set(
      /remove unit/i,
      () => ({ properties: { unit: '' } })
    );

    this.commandPatterns.set(
      /(?:set |change )?unit (?:color|colour) (?:to )?(.+)/i,
      (match) => {
        const colorInput = match[1].trim().toLowerCase();
        // Check for semantic colors first (Phase 1.3 support)
        const semanticColor = this.semanticColors[colorInput];
        if (semanticColor) {
          return { properties: { unitColor: semanticColor } };
        }
        const colorValue = this.parseColorValue(match[1]);
        return colorValue ? { properties: { unitColor: colorValue } } : {};
      }
    );

    this.commandPatterns.set(
      /(?:set |change )?unit (?:font )?size (?:to )?(\d+(?:\.\d+)?)(px|rem|em)?/i,
      (match) => ({ properties: { unitFontSize: `${match[1]}${match[2] || 'px'}` } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // PREFIX/SUFFIX COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |add |change )?prefix (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { prefix: match[1] } })
    );

    // Unquoted prefix catch-all: "add prefix $", "set prefix to #"
    this.commandPatterns.set(
      /(?:set |add |change )?prefix (?:to )?([^\s"']+)/i,
      (match) => ({ properties: { prefix: match[1] } })
    );

    this.commandPatterns.set(
      /remove prefix/i,
      () => ({ properties: { prefix: '' } })
    );

    this.commandPatterns.set(
      /(?:set |add |change )?suffix (?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { suffix: match[1] } })
    );

    // Unquoted suffix catch-all: "add suffix PSI", "set suffix to /hr"
    this.commandPatterns.set(
      /(?:set |add |change )?suffix (?:to )?([^\s"']+)/i,
      (match) => ({ properties: { suffix: match[1] } })
    );

    this.commandPatterns.set(
      /remove suffix/i,
      () => ({ properties: { suffix: '' } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // UNIT GAP COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |change )?unit (?:gap|spacing) (?:to )?(\d+)/i,
      (match) => ({ properties: { unitGap: parseInt(match[1], 10) } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // FORMATTING COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /format (?:as |like )?(currency|money)/i,
      () => ({ properties: { valueFormatter: 'currency' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(percent|percentage)/i,
      () => ({ properties: { valueFormatter: 'percent' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(number|numeric)/i,
      () => ({ properties: { valueFormatter: 'number' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(compact|abbreviated|short)/i,
      () => ({ properties: { valueFormatter: 'compact' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(integer|whole number)/i,
      () => ({ properties: { valueFormatter: 'integer' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(datetime|date ?time)/i,
      () => ({ properties: { valueFormatter: 'datetime' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(date)/i,
      () => ({ properties: { valueFormatter: 'date' } })
    );

    this.commandPatterns.set(
      /format (?:as |like )?(time)/i,
      () => ({ properties: { valueFormatter: 'time' } })
    );

    this.commandPatterns.set(
      /(?:show |set |use )?(\d+) decimals?/i,
      (match) => ({ properties: { decimalPlaces: parseInt(match[1], 10) } })
    );

    this.commandPatterns.set(
      /(?:set )?decimal (?:places )?(?:to )?(\d+)/i,
      (match) => ({ properties: { decimalPlaces: parseInt(match[1], 10) } })
    );

    this.commandPatterns.set(
      /(?:remove|hide)\s+thousands? separator/i,
      () => ({ properties: { thousandsSeparator: false } })
    );

    this.commandPatterns.set(
      /(?:add |show |use )?thousands? separator/i,
      () => ({ properties: { thousandsSeparator: true } })
    );

    this.commandPatterns.set(
      /(?:set |change )?currency (?:symbol )?(?:to )?["']?([^"'\s]+)["']?/i,
      (match) => ({ properties: { currencySymbol: match[1], valueFormatter: 'currency' } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // TYPOGRAPHY COMMANDS (VALUE)
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |change )?(?:font )?size (?:to )?(\d+(?:\.\d+)?)(px|rem|em)?/i,
      (match) => ({ properties: { fontSize: `${match[1]}${match[2] || 'px'}` } })
    );

    // Note: Percentage-based patterns (Phase 1.2) are defined later
    // These patterns use negative lookbehind to NOT match when preceded by a percentage
    this.commandPatterns.set(
      /(?<!\d+%\s*)(bigger|larger)/i,
      (match, component) => {
        const currentSize = parseInt(String(component.properties?.fontSize ?? '16'), 10);
        return { properties: { fontSize: `${currentSize + 4}px` } };
      }
    );

    this.commandPatterns.set(
      /(?<!\d+%\s*)(smaller)/i,
      (match, component) => {
        const currentSize = parseInt(String(component.properties?.fontSize ?? '16'), 10);
        return { properties: { fontSize: `${Math.max(8, currentSize - 4)}px` } };
      }
    );

    // Note: Uses negative lookbehind to NOT match "semibold" (only matches standalone "bold")
    this.commandPatterns.set(
      /(?<!semi)(bold|bolder)\b/i,
      () => ({ properties: { fontWeight: 700 } })
    );

    this.commandPatterns.set(
      /(?:make )?(?:text |value )?(normal|regular)/i,
      () => ({ properties: { fontWeight: 400 } })
    );

    this.commandPatterns.set(
      /(?:set |change )?(?:font )?weight (?:to )?(\d+)/i,
      (match) => {
        const weight = parseInt(match[1], 10);
        return { properties: { fontWeight: Math.min(900, Math.max(100, weight)) } };
      }
    );

    // Negative lookahead excludes "color code" to avoid matching Phase 4 conditional formatting commands
    this.commandPatterns.set(
      /(?:set |change )?(?:text |value )?(?:color|colour) (?!code)(?:to )?(.+)/i,
      (match) => {
        const colorInput = match[1].trim().toLowerCase();
        // Check for semantic colors first (Phase 1.3 support)
        const semanticColor = this.semanticColors[colorInput];
        if (semanticColor) {
          return { properties: { color: semanticColor } };
        }
        const colorValue = this.parseColorValue(match[1]);
        return colorValue ? { properties: { color: colorValue } } : {};
      }
    );

    this.commandPatterns.set(
      /align (?:text )?(left|center|centre|right)/i,
      (match) => {
        const alignment = match[1].toLowerCase() === 'centre' ? 'center' : match[1].toLowerCase();
        return { properties: { textAlign: alignment as 'left' | 'center' | 'right' } };
      }
    );

    this.commandPatterns.set(
      /(?:text )?align(?:ment)? (?:to )?(left|center|centre|right)/i,
      (match) => {
        const alignment = match[1].toLowerCase() === 'centre' ? 'center' : match[1].toLowerCase();
        return { properties: { textAlign: alignment as 'left' | 'center' | 'right' } };
      }
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // PHASE 1.1: NAMED FONT SIZE PRESETS
    // ═══════════════════════════════════════════════════════════════════════════

    // Pattern: "use large text", "tiny font", "massive size"
    const sizePresetKeys = Object.keys(this.fontSizePresets).join('|');
    this.commandPatterns.set(
      new RegExp(`(?:use |set )?(?:a )?(${sizePresetKeys})\\s+(?:text|font|size)`, 'i'),
      (match) => {
        const preset = match[1].toLowerCase();
        const size = this.fontSizePresets[preset];
        return size ? { properties: { fontSize: size } } : {};
      }
    );

    // Pattern: "text large", "font tiny" (reversed keyword order)
    this.commandPatterns.set(
      new RegExp(`(?:text|font|size)\\s+(${sizePresetKeys})(?:\\s|$)`, 'i'),
      (match) => {
        const preset = match[1].toLowerCase();
        const size = this.fontSizePresets[preset];
        return size ? { properties: { fontSize: size } } : {};
      }
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // PHASE 1.2: PERCENTAGE-BASED SIZE ADJUSTMENTS
    // ═══════════════════════════════════════════════════════════════════════════

    // Pattern: "make 50% bigger", "50% smaller"
    this.commandPatterns.set(
      /(?:make )?(\d+)%\s+(bigger|larger|smaller)/i,
      (match, component) => {
        const percent = parseInt(match[1], 10) / 100;
        const currentSize = parseInt(String(component.properties?.fontSize ?? '16'), 10);
        const multiplier = match[2].toLowerCase() === 'smaller' ? (1 - percent) : (1 + percent);
        const newSize = Math.max(8, Math.min(128, Math.round(currentSize * multiplier)));
        return { properties: { fontSize: `${newSize}px` } };
      }
    );

    // Pattern: "double the size", "double the font"
    this.commandPatterns.set(
      /double\s+(?:the\s+)?(?:size|font|text)/i,
      (match, component) => {
        const currentSize = parseInt(String(component.properties?.fontSize ?? '16'), 10);
        const newSize = Math.min(128, currentSize * 2);
        return { properties: { fontSize: `${newSize}px` } };
      }
    );

    // Pattern: "halve the size", "halve the font"
    this.commandPatterns.set(
      /halve\s+(?:the\s+)?(?:size|font|text)/i,
      (match, component) => {
        const currentSize = parseInt(String(component.properties?.fontSize ?? '16'), 10);
        const newSize = Math.max(8, Math.round(currentSize / 2));
        return { properties: { fontSize: `${newSize}px` } };
      }
    );

    // Pattern: "increase size by 50%", "decrease font by 25%"
    this.commandPatterns.set(
      /(increase|decrease)\s+(?:the\s+)?(?:size|font|text)\s+by\s+(\d+)%/i,
      (match, component) => {
        const percent = parseInt(match[2], 10) / 100;
        const currentSize = parseInt(String(component.properties?.fontSize ?? '16'), 10);
        const multiplier = match[1].toLowerCase() === 'decrease' ? (1 - percent) : (1 + percent);
        const newSize = Math.max(8, Math.min(128, Math.round(currentSize * multiplier)));
        return { properties: { fontSize: `${newSize}px` } };
      }
    );

    // Pattern: "scale up 150%", "scale down 75%"
    this.commandPatterns.set(
      /scale\s+(up|down)\s+(?:to\s+)?(\d+)%/i,
      (match, component) => {
        const percent = parseInt(match[2], 10) / 100;
        const currentSize = parseInt(String(component.properties?.fontSize ?? '16'), 10);
        // "scale up 150%" means multiply by 1.5, "scale down 75%" means multiply by 0.75
        const newSize = Math.max(8, Math.min(128, Math.round(currentSize * percent)));
        return { properties: { fontSize: `${newSize}px` } };
      }
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // PHASE 1.3: SEMANTIC COLOR SUPPORT
    // ═══════════════════════════════════════════════════════════════════════════

    // Pattern: "label success color", "label success", "unit warning color", "unit warning"
    // This pattern MUST come before the generic semantic color patterns to avoid conflicts
    const semanticColorKeys = Object.keys(this.semanticColors).join('|');
    this.commandPatterns.set(
      new RegExp(`(label|unit)\\s+(${semanticColorKeys})(?:\\s+colou?r)?(?:\\s|$)`, 'i'),
      (match) => {
        const target = match[1].toLowerCase();
        const semantic = match[2].toLowerCase();
        const color = this.semanticColors[semantic];
        if (!color) return {};
        return target === 'label'
          ? { properties: { labelColor: color } }
          : { properties: { unitColor: color } };
      }
    );

    // Pattern: "use success color", "set error color"
    // Uses negative lookbehind to not match if preceded by "label " or "unit "
    this.commandPatterns.set(
      new RegExp(`(?<!(?:label|unit)\\s)(?:use |set )?(?:a )?(${semanticColorKeys})\\s+colou?r`, 'i'),
      (match) => {
        const semantic = match[1].toLowerCase();
        const color = this.semanticColors[semantic];
        return color ? { properties: { color } } : {};
      }
    );

    // Pattern: "color success", "colour error"
    this.commandPatterns.set(
      new RegExp(`colou?r\\s+(${semanticColorKeys})(?:\\s|$)`, 'i'),
      (match) => {
        const semantic = match[1].toLowerCase();
        const color = this.semanticColors[semantic];
        return color ? { properties: { color } } : {};
      }
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // PHASE 1.4: NAMED FONT WEIGHT PRESETS
    // ═══════════════════════════════════════════════════════════════════════════

    // Pattern: "use light font", "set semibold weight"
    const weightPresetKeys = Object.keys(this.fontWeightPresets).filter(k => k !== 'normal' && k !== 'bold').join('|');
    this.commandPatterns.set(
      new RegExp(`(?:use |set )?(?:a )?(${weightPresetKeys})\\s+(?:font|weight)`, 'i'),
      (match) => {
        const preset = match[1].toLowerCase();
        const weight = this.fontWeightPresets[preset];
        return weight !== undefined ? { properties: { fontWeight: weight } } : {};
      }
    );

    // Pattern: "font light", "weight semibold"
    this.commandPatterns.set(
      new RegExp(`(?:font|weight)\\s+(${weightPresetKeys})(?:\\s|$)`, 'i'),
      (match) => {
        const preset = match[1].toLowerCase();
        const weight = this.fontWeightPresets[preset];
        return weight !== undefined ? { properties: { fontWeight: weight } } : {};
      }
    );

    // Pattern: "label semibold", "label thin"
    this.commandPatterns.set(
      new RegExp(`label\\s+(${weightPresetKeys})(?:\\s|$)`, 'i'),
      (match) => {
        const preset = match[1].toLowerCase();
        const weight = this.fontWeightPresets[preset];
        return weight !== undefined ? { properties: { labelFontWeight: weight } } : {};
      }
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // LAYOUT COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:use |set )?inline (?:layout)?/i,
      () => ({ properties: { layout: 'inline' } })
    );

    this.commandPatterns.set(
      /(?:use |set )?stacked (?:layout)?/i,
      () => ({ properties: { layout: 'stacked' } })
    );

    this.commandPatterns.set(
      /(?:set |change )?gap (?:to )?(\d+)/i,
      (match) => ({ properties: { gap: parseInt(match[1], 10) } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // STATE COMMANDS
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /show loading(?: state)?/i,
      () => ({ properties: { showLoadingState: true } })
    );

    this.commandPatterns.set(
      /hide loading(?: state)?/i,
      () => ({ properties: { showLoadingState: false } })
    );

    this.commandPatterns.set(
      /(?:set |change )?loading (?:text )?(?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { loadingText: match[1] } })
    );

    this.commandPatterns.set(
      /show error(?: state)?/i,
      () => ({ properties: { showErrorState: true } })
    );

    this.commandPatterns.set(
      /hide error(?: state)?/i,
      () => ({ properties: { showErrorState: false } })
    );

    this.commandPatterns.set(
      /(?:set |change )?error (?:text )?(?:to )?["']([^"']+)["']/i,
      (match) => ({ properties: { errorText: match[1] } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // DATA BINDING (INFORMATIONAL - guides to Properties Panel)
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:set |change )?value ?field (?:to )?["']?([a-zA-Z0-9_]+)["']?/i,
      (match) => ({ properties: { valueField: match[1] } })
    );

    this.commandPatterns.set(
      /(?:set |change )?label ?field (?:to )?["']?([a-zA-Z0-9_]+)["']?/i,
      (match) => ({ properties: { labelField: match[1] } })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // DATA DISCONNECTION (Convert DATA_TEXT → TEXT)
    // These patterns trigger conversion back to static text
    // ═══════════════════════════════════════════════════════════════════════════
    this.commandPatterns.set(
      /(?:disconnect|remove|unbind)\s+data/i,
      () => ({ convertToText: true })
    );

    this.commandPatterns.set(
      /(?:make|convert)\s+(?:it\s+)?(?:to\s+)?static/i,
      () => ({ convertToText: true })
    );

    this.commandPatterns.set(
      /(?:remove|clear)\s+data\s*(?:source|binding)/i,
      () => ({ convertToText: true })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // PHASE 4.1: CONDITIONAL FORMATTING (Value-Based Colors)
    // ═══════════════════════════════════════════════════════════════════════════

    // Pattern: "red when negative", "green for positive", "blue if zero"
    this.commandPatterns.set(
      /(?:(?:use |set )?)?([a-z]+|#[0-9A-Fa-f]{3,6})\s+(?:when|for|if)\s+(negative|positive|zero)/i,
      (match) => {
        const colorValue = this.resolveColorValue(match[1]);
        const condition = match[2].toLowerCase();
        if (!colorValue) return {};
        const props: Record<string, unknown> = { useConditionalColor: true };
        if (condition === 'negative') props.negativeColor = colorValue;
        else if (condition === 'positive') props.positiveColor = colorValue;
        else if (condition === 'zero') props.zeroColor = colorValue;
        return { properties: props };
      }
    );

    // Pattern: "negative red positive green" (compound setting) - MUST come before simpler pattern
    this.commandPatterns.set(
      /(negative|positive)\s+([a-z]+|#[0-9A-Fa-f]{3,6})\s+(?:and\s+)?(negative|positive)\s+([a-z]+|#[0-9A-Fa-f]{3,6})/i,
      (match) => {
        const cond1 = match[1].toLowerCase();
        const color1 = this.resolveColorValue(match[2]);
        const cond2 = match[3].toLowerCase();
        const color2 = this.resolveColorValue(match[4]);
        if (!color1 || !color2) return {};
        const props: Record<string, unknown> = { useConditionalColor: true };
        if (cond1 === 'negative') props.negativeColor = color1;
        else if (cond1 === 'positive') props.positiveColor = color1;
        if (cond2 === 'negative') props.negativeColor = color2;
        else if (cond2 === 'positive') props.positiveColor = color2;
        return { properties: props };
      }
    );

    // Pattern: "negative red", "positive green", "zero gray" (alternate syntax)
    // Negative lookahead ensures we don't match compound patterns
    this.commandPatterns.set(
      /(negative|positive|zero)\s+([a-z]+|#[0-9A-Fa-f]{3,6})(?!\s+(?:and\s+)?(?:negative|positive|zero))(?:\s|$)/i,
      (match) => {
        const condition = match[1].toLowerCase();
        const colorValue = this.resolveColorValue(match[2]);
        if (!colorValue) return {};
        const props: Record<string, unknown> = { useConditionalColor: true };
        if (condition === 'negative') props.negativeColor = colorValue;
        else if (condition === 'positive') props.positiveColor = colorValue;
        else if (condition === 'zero') props.zeroColor = colorValue;
        return { properties: props };
      }
    );

    // Pattern: "enable conditional formatting", "use value-based coloring"
    this.commandPatterns.set(
      /(?:enable|use|turn on)\s+(?:conditional\s+(?:formatting|colors?)|value[- ]?based\s+colou?ring?)/i,
      () => ({
        properties: {
          useConditionalColor: true,
          positiveColor: '#059669',
          negativeColor: '#DC2626',
          zeroColor: '#6B7280'
        }
      })
    );

    // Pattern: "disable conditional formatting"
    this.commandPatterns.set(
      /(?:disable|turn off|remove)\s+conditional\s+(?:formatting|colors?)/i,
      () => ({ properties: { useConditionalColor: false } })
    );

    // Pattern: "color code values"
    this.commandPatterns.set(
      /colou?r\s+code\s+values?/i,
      () => ({
        properties: {
          useConditionalColor: true,
          positiveColor: '#059669',
          negativeColor: '#DC2626',
          zeroColor: '#6B7280'
        }
      })
    );

    // ═══════════════════════════════════════════════════════════════════════════
    // PHASE 4.2: VALUE ANIMATION
    // ═══════════════════════════════════════════════════════════════════════════

    // Pattern: "enable animation", "animate value changes"
    this.commandPatterns.set(
      /(?:enable|turn on|use)\s+(?:value\s+)?animation/i,
      () => ({
        properties: {
          animateValueChange: true,
          animationType: 'fade',
          animationDuration: 300
        }
      })
    );

    // Pattern: "animate value changes"
    this.commandPatterns.set(
      /animate\s+(?:value\s+)?(?:changes?|transitions?)/i,
      () => ({
        properties: {
          animateValueChange: true,
          animationType: 'fade',
          animationDuration: 300
        }
      })
    );

    // Pattern: "disable animation"
    this.commandPatterns.set(
      /(?:disable|turn off|remove)\s+(?:value\s+)?animation/i,
      () => ({ properties: { animateValueChange: false } })
    );

    // Pattern: "use fade/slide/count/scale animation"
    this.commandPatterns.set(
      /(?:use\s+)?(fade|slide|count|scale)\s+animation/i,
      (match) => ({
        properties: {
          animateValueChange: true,
          animationType: match[1].toLowerCase() as 'fade' | 'slide' | 'count' | 'scale'
        }
      })
    );

    // Pattern: "animation duration 500ms", "animation duration 1s"
    this.commandPatterns.set(
      /animation\s+(?:duration\s+)?(\d+)\s*(?:ms|milliseconds?)/i,
      (match) => {
        let duration = parseInt(match[1], 10);
        duration = Math.max(100, Math.min(2000, duration)); // Clamp to 100-2000ms
        return { properties: { animationDuration: duration, animateValueChange: true } };
      }
    );

    // Pattern: "animation 1 second", "animation 2 seconds"
    this.commandPatterns.set(
      /animation\s+(?:duration\s+)?(\d+(?:\.\d+)?)\s*(?:s|seconds?)/i,
      (match) => {
        let duration = Math.round(parseFloat(match[1]) * 1000);
        duration = Math.max(100, Math.min(2000, duration)); // Clamp to 100-2000ms
        return { properties: { animationDuration: duration, animateValueChange: true } };
      }
    );

    // Pattern: "fast animation", "slow animation"
    this.commandPatterns.set(
      /(fast|slow|quick)\s+animation/i,
      (match) => {
        const speed = match[1].toLowerCase();
        const duration = speed === 'slow' ? 600 : 150; // fast/quick = 150ms, slow = 600ms
        return {
          properties: {
            animateValueChange: true,
            animationDuration: duration
          }
        };
      }
    );

    // Pattern: "animation easing ease-in", "ease-out animation"
    this.commandPatterns.set(
      /(?:animation\s+)?(?:easing\s+)?(ease|ease-in|ease-out|ease-in-out|linear)(?:\s+(?:animation|easing))?/i,
      (match) => ({
        properties: {
          animateValueChange: true,
          animationEasing: match[1].toLowerCase() as 'ease' | 'ease-in' | 'ease-out' | 'ease-in-out' | 'linear'
        }
      })
    );
  }

  /**
   * Resolves color names (including semantic colors) to hex values
   */
  private resolveColorValue(colorInput: string): string | null {
    const color = colorInput.toLowerCase().trim();

    // Check semantic colors first
    if (this.semanticColors[color]) {
      return this.semanticColors[color];
    }

    // Check basic named colors
    const basicColors: Record<string, string> = {
      red: '#DC2626', green: '#059669', blue: '#2563EB',
      yellow: '#EAB308', orange: '#EA580C', purple: '#7C3AED',
      pink: '#EC4899', gray: '#6B7280', grey: '#6B7280',
      black: '#000000', white: '#FFFFFF', cyan: '#06B6D4',
      teal: '#14B8A6', indigo: '#4F46E5', lime: '#84CC16',
    };
    if (basicColors[color]) {
      return basicColors[color];
    }

    // Check if it's already a hex color
    if (/^#[0-9A-Fa-f]{3,6}$/.test(colorInput)) {
      return colorInput;
    }

    return null;
  }

  canHandle(command: string, component: Component): boolean {
    return component.type === ComponentType.DATA_TEXT && this.hasDataTextKeywords(command);
  }

  private hasDataTextKeywords(command: string): boolean {
    const keywords = [
      // Original keywords
      "data text", "datatext", "value", "label", "unit", "prefix", "suffix",
      "format", "decimal", "currency", "percent", "thousands",
      "font", "size", "bold", "color", "colour", "weight", "align", "bigger", "larger", "smaller",
      "inline", "stacked", "gap", "loading", "error", "empty", "placeholder",
      "connect", "field", "data", "database",
      // Phase 1.1: Size presets
      "tiny", "small", "medium", "large", "huge", "massive", "giant", "xs", "sm", "lg", "xl", "xxl", "3xl",
      // Phase 1.2: Percentage keywords
      "double", "halve", "increase", "decrease", "scale",
      // Phase 1.3: Semantic colors
      "success", "positive", "good", "profit", "danger", "negative", "loss",
      "warning", "caution", "alert", "info", "primary", "neutral", "muted", "secondary",
      // Phase 1.4: Weight presets
      "thin", "hairline", "light", "semibold", "heavy", "black",
      // Phase 4.1: Conditional formatting
      "conditional", "when negative", "when positive", "if negative", "for positive",
      "color code", "value-based",
      // Phase 4.2: Animation
      "animate", "animation", "transition", "fade", "slide", "count", "duration",
      // Unit gap keywords
      "unit gap", "unit spacing",
      // Data disconnection keywords
      "disconnect", "unbind", "static", "remove data", "clear data"
    ];
    const lowerCommand = command.toLowerCase();
    return keywords.some(keyword => lowerCommand.includes(keyword));
  }

  async parseCommand(command: string, component: Component): Promise<CommandResult> {
    // Handle data connection commands specially
    if (this.isDataConnectionCommand(command)) {
      return {
        success: false,
        error: "Data connections must be configured via the Properties Panel. Select the component and use the 'Data Source' section to connect to a data query.",
        suggestions: await this.getSuggestions(component)
      };
    }

    // Split complex commands
    const commandParts = command.split(/\s+(?:and|with|also)\s+/i);
    const changes: PropertyChange = { properties: {}, style: {} };

    for (const part of commandParts) {
      // Try pattern matching
      const patternMatch = this.matchPatterns(part, component);
      if (patternMatch) {
        // Check for type conversion flag (DATA_TEXT → TEXT)
        if ('convertToText' in patternMatch && patternMatch.convertToText) {
          changes.convertToText = true;
        }
        if (patternMatch.properties) {
          changes.properties = { ...changes.properties, ...patternMatch.properties };
        }
        if (patternMatch.style) {
          changes.style = { ...changes.style, ...patternMatch.style };
        }
      }

      // Parse colors not caught by patterns
      const colorValue = this.parseColorValue(part);
      if (colorValue && !patternMatch) {
        if (part.toLowerCase().includes("background") || part.toLowerCase().includes("bg")) {
          changes.style = { ...changes.style, backgroundColor: colorValue };
        } else if (part.toLowerCase().includes("label")) {
          changes.properties = { ...changes.properties, labelColor: colorValue };
        } else if (part.toLowerCase().includes("unit")) {
          changes.properties = { ...changes.properties, unitColor: colorValue };
        } else if (part.toLowerCase().includes("text") || part.toLowerCase().includes("color") || part.toLowerCase().includes("colour")) {
          changes.properties = { ...changes.properties, color: colorValue };
        }
      }

      // Parse padding
      if (part.includes("padding")) {
        const paddingValue = this.parseNumericValue(part);
        if (paddingValue) {
          changes.style = { ...changes.style, padding: paddingValue };
        }
      }
    }

    // Check if we found any changes (including type conversion)
    if (changes.convertToText ||
        Object.keys(changes.properties || {}).length > 0 ||
        Object.keys(changes.style || {}).length > 0) {

      const validation = this.validateChanges(changes, component);
      if (!validation.valid) {
        return {
          success: false,
          error: validation.error,
          suggestions: await this.getSuggestions(component)
        };
      }

      // Handle DATA_TEXT → TEXT conversion specially
      const description = changes.convertToText
        ? 'Convert to static text and disconnect data binding'
        : this.generateDescription(changes, component);

      return {
        success: true,
        command: {
          type: changes.convertToText ? 'conversion' : this.determineCommandType(changes),
          changes,
          confidence: this.calculateConfidence(command, changes),
          description
        },
        suggestions: await this.getSuggestions(component)
      };
    }

    return {
      success: false,
      error: "Could not understand the data text command",
      suggestions: await this.getSuggestions(component)
    };
  }

  private isDataConnectionCommand(command: string): boolean {
    const dataConnectPatterns = [
      /connect to (?:data|database|query)/i,
      /link to (?:data|database|query)/i,
      /bind to (?:data|database|query)/i,
      /(?:set|change) data ?source/i
    ];
    return dataConnectPatterns.some(pattern => pattern.test(command));
  }

  async getExampleCommands(): Promise<string[]> {
    return [
      // Value commands
      'Set value to "42"',
      'Set value to 1234.56',
      'Set empty value to "N/A"',

      // Label commands
      'Set label to "Temperature"',
      'Show label',
      'Hide label',
      'Put label above',
      'Put label on left',
      'Set label color to gray',
      'Set label size to 12px',
      'Make label bold',

      // Unit commands
      'Set unit to "°C"',
      'Set unit to %',
      'Add unit PSI',
      'Set unit to kPa',
      'Remove unit',
      'Set unit color to blue',

      // Prefix/Suffix
      'Set prefix to "$"',
      'Add prefix $',
      'Set suffix to "USD"',
      'Add suffix PSI',
      'Remove prefix',

      // Formatting
      'Format as currency',
      'Format as percent',
      'Format as number',
      'Format as compact',
      'Format as integer',
      'Format as date',
      'Show 2 decimals',
      'Set decimal places to 0',
      'Add thousands separator',
      'Set currency symbol to "€"',

      // Typography
      'Set font size to 24px',
      'Make text bigger',
      'Make text smaller',
      'Make text bold',
      'Set color to blue',
      'Align center',
      'Align left',

      // Layout
      'Use inline layout',
      'Use stacked layout',
      'Set gap to 8',

      // States
      'Show loading state',
      'Hide loading state',
      'Set loading text to "Fetching..."',
      'Show error state',
      'Set error text to "Failed to load"',

      // Combined commands
      'Set value to 1234 and format as currency',
      'Make bold and set color to blue',
      'Set label to "Price" and put label above'
    ];
  }

  async getSuggestions(component: Component): Promise<string[]> {
    const suggestions: string[] = [];
    const props = component.properties || {};

    // Value suggestions
    if (!props.value && props.value !== 0) {
      suggestions.push('Set value to "42"', 'Set a static value');
    }

    // Label suggestions
    if (!props.label) {
      suggestions.push('Set label to "Label"', 'Add a label');
    } else {
      if (props.showLabel === false) {
        suggestions.push('Show label');
      } else {
        suggestions.push('Hide label');
      }

      const currentPosition = props.labelPosition || 'above';
      if (currentPosition !== 'left') {
        suggestions.push('Put label on left');
      }
      if (currentPosition !== 'above') {
        suggestions.push('Put label above');
      }
    }

    // Formatting suggestions
    const currentFormatter = props.valueFormatter;
    if (currentFormatter !== 'currency') {
      suggestions.push('Format as currency');
    }
    if (currentFormatter !== 'percent') {
      suggestions.push('Format as percent');
    }
    if (!props.thousandsSeparator) {
      suggestions.push('Add thousands separator');
    }
    if (props.decimalPlaces === undefined) {
      suggestions.push('Show 2 decimals');
    }

    // Unit suggestions
    if (!props.unit) {
      suggestions.push('Add unit °C', 'Add unit PSI');
    } else {
      suggestions.push('Remove unit', 'Change unit');
    }
    if (props.unit || props.suffix) {
      suggestions.push('Set unit gap to 4');
    }

    // Typography suggestions
    const currentWeight = Number(props.fontWeight) || 400;
    if (currentWeight < 700) {
      suggestions.push('Make text bold');
    } else {
      suggestions.push('Make text normal');
    }
    suggestions.push('Make text bigger', 'Make text smaller');

    // Layout suggestions
    const currentLayout = props.layout || 'stacked';
    if (currentLayout !== 'inline') {
      suggestions.push('Use inline layout');
    } else {
      suggestions.push('Use stacked layout');
    }

    // Alignment suggestions
    const currentAlign = props.textAlign || 'left';
    if (currentAlign !== 'center') {
      suggestions.push('Align center');
    }
    if (currentAlign !== 'right') {
      suggestions.push('Align right');
    }

    // Color suggestions
    if (!props.color) {
      suggestions.push('Set color to blue');
    }

    return suggestions.slice(0, 12);
  }

  protected override validateChanges(changes: PropertyChange, component: Component): { valid: boolean; error?: string } {
    // Validate valueFormatter
    if (changes.properties?.valueFormatter) {
      const validFormatters = ['number', 'currency', 'percent', 'date', 'time', 'datetime', 'compact', 'integer', 'decimal', 'custom'];
      const formatter = String(changes.properties.valueFormatter);
      if (!validFormatters.includes(formatter)) {
        return { valid: false, error: `Invalid formatter: ${formatter}. Must be one of: ${validFormatters.join(', ')}` };
      }
    }

    // Validate labelPosition
    if (changes.properties?.labelPosition) {
      const validPositions = ['above', 'below', 'left', 'right'];
      const position = String(changes.properties.labelPosition);
      if (!validPositions.includes(position)) {
        return { valid: false, error: `Invalid label position: ${position}. Must be one of: ${validPositions.join(', ')}` };
      }
    }

    // Validate layout
    if (changes.properties?.layout) {
      const validLayouts = ['inline', 'stacked'];
      const layout = String(changes.properties.layout);
      if (!validLayouts.includes(layout)) {
        return { valid: false, error: `Invalid layout: ${layout}. Must be one of: ${validLayouts.join(', ')}` };
      }
    }

    // Validate textAlign
    if (changes.properties?.textAlign) {
      const validAlignments = ['left', 'center', 'right'];
      const alignment = String(changes.properties.textAlign);
      if (!validAlignments.includes(alignment)) {
        return { valid: false, error: `Invalid text alignment: ${alignment}. Must be one of: ${validAlignments.join(', ')}` };
      }
    }

    // Validate decimalPlaces
    if (changes.properties?.decimalPlaces !== undefined) {
      const decimals = Number(changes.properties.decimalPlaces);
      if (isNaN(decimals) || decimals < 0 || decimals > 10) {
        return { valid: false, error: `Invalid decimal places: ${changes.properties.decimalPlaces}. Must be a number between 0 and 10.` };
      }
    }

    // Validate fontWeight
    if (changes.properties?.fontWeight !== undefined) {
      const weight = Number(changes.properties.fontWeight);
      if (isNaN(weight) || weight < 100 || weight > 900) {
        return { valid: false, error: `Invalid font weight: ${changes.properties.fontWeight}. Must be a number between 100 and 900.` };
      }
    }

    // Validate labelFontWeight
    if (changes.properties?.labelFontWeight !== undefined) {
      const weight = Number(changes.properties.labelFontWeight);
      if (isNaN(weight) || weight < 100 || weight > 900) {
        return { valid: false, error: `Invalid label font weight: ${changes.properties.labelFontWeight}. Must be a number between 100 and 900.` };
      }
    }

    // Validate gap
    if (changes.properties?.gap !== undefined) {
      const gap = Number(changes.properties.gap);
      if (isNaN(gap) || gap < 0) {
        return { valid: false, error: `Invalid gap: ${changes.properties.gap}. Must be a non-negative number.` };
      }
    }

    // Validate unitGap
    if (changes.properties?.unitGap !== undefined) {
      const unitGap = Number(changes.properties.unitGap);
      if (isNaN(unitGap) || unitGap < 0 || unitGap > 24) {
        return { valid: false, error: `Invalid unit gap: ${changes.properties.unitGap}. Must be a number between 0 and 24.` };
      }
    }

    return super.validateChanges(changes, component);
  }
}

export const dataTextCommandHandler = new DataTextCommandHandler();
